import React, { useEffect, useMemo, useState } from 'react';
import { Copy, Loader2, Send, Sparkles } from 'lucide-react';
import { DialogShell, fieldClass, ghostBtn, iconBtn, primaryBtn } from '../components/dataModelsUi';
import { logDataModels } from '../dataModelsLogger';
import { copilotExpressionService } from './copilotExpressionService';
import { validateExpression } from './parser';
import type { PolicyAsset, PolicyType } from './types';
import { POLICY_TYPE_LABEL } from './types';
import { assetFieldLabel, usePolicyAssetColumns } from './usePolicyAssetColumns';

export default function ExpressionCopilotDialog({
  isLight,
  policyType,
  assets,
  currentExpression,
  readOnly,
  onClose,
  onUse,
  onCopy,
}: {
  isLight: boolean;
  policyType: PolicyType;
  assets: PolicyAsset[];
  currentExpression: string;
  readOnly?: boolean;
  onClose: () => void;
  onUse: (expression: string) => void;
  onCopy: (text: string) => void;
}) {
  const { assets: withColumns, loading: columnsLoading, error: columnsError } = usePolicyAssetColumns(assets, { enabled: true });
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [expression, setExpression] = useState('');
  const [explanation, setExplanation] = useState('');
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const ink = isLight ? 'text-[#1C1C1C]' : 'text-white';
  const panel = isLight ? 'border-[#E6E8EB] bg-[#F7F8FA]' : 'border-white/10 bg-white/[0.03]';
  const tables = withColumns.filter((asset) => asset.name);
  const validation = useMemo(
    () => (expression ? validateExpression(expression, withColumns) : null),
    [expression, withColumns],
  );

  useEffect(() => {
    logDataModels('app', 'governance_expression_copilot_opened', {
      policyType,
      assets: assets.length,
    });
  }, [assets.length, policyType]);

  const send = async () => {
    const trimmed = prompt.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError('');
    try {
      const result = await copilotExpressionService.suggest({
        prompt: trimmed,
        policyType,
        currentExpression,
        priorExpression: expression || undefined,
        assets: withColumns,
      });
      if (!result.expression) {
        setError('The copilot did not return an expression. Try a more specific request.');
        return;
      }
      setExpression(result.expression);
      setExplanation(result.explanation);
      setPrompt('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Copilot request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogShell
      title="Expression copilot"
      subtitle={`Describe the filter you need. The copilot uses ${POLICY_TYPE_LABEL[policyType]} rules and the selected tables.`}
      isLight={isLight}
      onClose={onClose}
      size="medium"
      footer={(
        <>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>Close</button>
          {!readOnly && expression ? (
            <button
              type="button"
              className={primaryBtn()}
              onClick={() => {
                onUse(expression);
                logDataModels('app', 'governance_expression_copilot_used', { policyType, length: expression.length });
              }}
            >
              Use expression
            </button>
          ) : null}
        </>
      )}
    >
      <div className="space-y-3">
        {tables.length ? (
          <div className="flex flex-wrap gap-1.5">
            {tables.map((asset) => (
              <span
                key={asset.id}
                className={`rounded-md border px-1.5 py-0.5 text-[11px] ${isLight ? 'border-[#E6E8EB] text-[#3C4048]' : 'border-white/10 text-slate-300'}`}
                title={`${assetFieldLabel(asset)} · ${asset.columns.length} columns`}
              >
                {assetFieldLabel(asset)}
                {asset.columns.length ? ` · ${asset.columns.length}` : ''}
              </span>
            ))}
          </div>
        ) : (
          <p className={`text-[12px] ${muted}`}>Select tables in Applies to so the copilot can use column names.</p>
        )}
        {columnsLoading ? <p className={`text-[12px] ${muted}`}>Loading columns…</p> : null}
        {columnsError ? <p className={`text-[12px] ${isLight ? 'text-rose-600' : 'text-rose-300'}`}>{columnsError}</p> : null}

        {expression ? (
          <div className={`space-y-2 rounded-md border px-3 py-2.5 ${panel}`}>
            <div className="flex items-center justify-between gap-2">
              <p className={`flex items-center gap-1.5 text-[12px] font-medium ${ink}`}>
                <Sparkles size={13} />
                Suggested expression
              </p>
              <button
                type="button"
                className={`${iconBtn(isLight)} !h-7 !w-7`}
                aria-label="Copy expression"
                title="Copy expression"
                onClick={() => {
                  onCopy(expression);
                  logDataModels('app', 'governance_expression_copilot_copied', { policyType, length: expression.length });
                }}
              >
                <Copy size={13} />
              </button>
            </div>
            <pre className={`overflow-x-auto whitespace-pre-wrap font-mono text-[12px] leading-5 ${ink}`}>{expression}</pre>
            {explanation ? <p className={`text-[12px] ${muted}`}>{explanation}</p> : null}
            {validation?.issues.length ? (
              <div className={`text-[11px] ${isLight ? 'text-amber-800' : 'text-amber-200'}`}>
                {validation.issues.map((issue) => (
                  <p key={`${issue.from}-${issue.message}`}>{issue.message}</p>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {busy ? (
          <div className={`flex items-center gap-2 text-[12px] ${muted}`}>
            <Loader2 size={14} className="animate-spin" />
            Drafting an expression from the selected tables…
          </div>
        ) : null}

        {error ? <p className={`text-[12px] ${isLight ? 'text-rose-600' : 'text-rose-300'}`}>{error}</p> : null}

        <form
          className="space-y-2"
          onSubmit={(event) => {
            event.preventDefault();
            void send();
          }}
        >
          <textarea
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            disabled={busy}
            rows={3}
            placeholder={expression ? 'Refine the expression…' : 'Example: only rows in the caller region, or hide salary unless HR'}
            className={`${fieldClass(isLight)} min-h-[84px] resize-y`}
          />
          <div className="flex justify-end">
            <button
              type="submit"
              className={primaryBtn()}
              disabled={!prompt.trim() || busy}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {expression ? 'Refine' : 'Generate'}
            </button>
          </div>
        </form>
      </div>
    </DialogShell>
  );
}
